export type ThemePreference = 'dark' | 'light' | 'system';
export type DownloadFormat = 'jpg' | 'png' | 'webp';

export type NexhancePreferences = {
  theme: ThemePreference;
  downloadFormat: DownloadFormat;
  rememberToolSettings: boolean;
  reducedAnimations: boolean;
};

const STORAGE_KEY = 'nexhance-preferences';
const LEGACY_STORAGE_KEY = 'lumen-preferences';
const THEME_KEY = 'nexhance-theme';
const LEGACY_THEME_KEY = 'lumen-theme';
export const THEME_EVENT = 'nexhance-theme-change';

const defaults: NexhancePreferences = {
  theme: 'dark',
  downloadFormat: 'jpg',
  rememberToolSettings: true,
  reducedAnimations: false
};

export function getPreferences(): NexhancePreferences {
  if (typeof window === 'undefined') return defaults;
  try {
    const raw = localStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) {
      const legacyTheme = localStorage.getItem(THEME_KEY) ?? localStorage.getItem(LEGACY_THEME_KEY);
      return { ...defaults, theme: legacyTheme === 'light' ? 'light' : 'dark' };
    }
    const parsed = JSON.parse(raw) as Partial<NexhancePreferences>;
    const merged = { ...defaults, ...parsed };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    return merged;
  } catch {
    return defaults;
  }
}

export function getResolvedTheme(preference = getPreferences().theme): 'dark' | 'light' {
  if (preference !== 'system') return preference;
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

export function applyThemePreference(preference = getPreferences().theme) {
  if (typeof document === 'undefined') return;
  const resolved = getResolvedTheme(preference);
  document.documentElement.dataset.theme = resolved;
  document.documentElement.dataset.themePreference = preference;
  document.documentElement.dataset.reducedMotion = getPreferences().reducedAnimations ? 'true' : 'false';
}

export function savePreferences(next: NexhancePreferences) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  localStorage.setItem(THEME_KEY, getResolvedTheme(next.theme));
  applyThemePreference(next.theme);
  window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: next }));
}

export function clearNexhanceLocalData() {
  const keys = Object.keys(localStorage).filter((key) =>
    key.startsWith('nexhance-') || key.startsWith('lumen-')
  );
  keys.forEach((key) => localStorage.removeItem(key));
  applyThemePreference(defaults.theme);
  window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: defaults }));
}
